import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  Output,
  TemplateRef
} from '@angular/core';
import {TreeViewItem} from '../tree-view/models/tree-view-item.interface';
import { NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { IconComponent } from '../icon/icon.component';
import { TooltipDirective } from '@aft/frogo-kit/directives/tooltip.directive';
import { TreeViewComponent } from '../tree-view/components/tree-view/tree-view-component';
import { DropdownDirective } from '@aft/frogo-kit/directives/dropdown.directive';

export interface IUserInvitation {
  accepted_at: string | null;
  created_at: string;
  description: string;
  id: string;
  org_id: string;
  rejected_at: string | null;
  role_id: string;
  user_id: string;
}

const DEFAULT_WIDTH = '256px';
const DEFAULT_MIN_WIDTH = '72px';

export interface SelectOption {
  id: string;
  label: string;
}

export interface ProfileInfo {
  name: string;
  email: string;
  photo?: string;
}

export interface LangOption {
  id: string;
  img: string;
}

export interface I18nKeys {
  "title": string,
  "accept": string,
  "reject": string,
}

const i18nTextDefault: I18nKeys = {
  "title": "Notifications",
  "accept": "Accept",
  "reject": "Reject"
}

@Component({
  selector: 'fk-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  imports: [
    NgIf,
    NgClass,
    NgFor,
    NgTemplateOutlet,
    IconComponent,
    TreeViewComponent,
    TooltipDirective,
    DropdownDirective
  ],
})
export class NavBarComponent {
  @Input()
  expanded = true;

  @Input()
  version = '';

  @Input()
  appName = '';

  @Input()
  menuItems: TreeViewItem[] = [];

  @Input()
  secondaryMenuItems: TreeViewItem[] = [];

  @Input()
  selectedOption!: string;

  @Input()
  selectOptions!: SelectOption[];

  @Input()
  profile!: ProfileInfo;

  @Input()
  invates!: IUserInvitation[];

  @Input()
  canAcceptInvites!: boolean;

  @Input()
  profileInfo!: ProfileInfo;

  @Input()
  public set i18n(data: I18nKeys) {
    this.i18nText = Object.assign({}, i18nTextDefault, data);
  }

  @Output()
  optionSelected = new EventEmitter<string>();

  @Output()
  acceptInvite = new EventEmitter<string>();

  @Output()
  rejectInvite = new EventEmitter<string>();

  @Output()
  openProfile = new EventEmitter<void>();

  @ContentChild('footer')
  headerTpl!: TemplateRef<any>;

  @ContentChild('footer')
  footerTpl!: TemplateRef<any>;

  @ContentChild('logoImg')
  logoImgTpl!: TemplateRef<any>;

  public i18nText: I18nKeys = i18nTextDefault;

  protected width = DEFAULT_WIDTH;
  protected selectExpanded = false;
  protected profileExpanded = false;
  protected notificationsExpanded = false;

  protected toggle(): void {
    this.expanded = !this.expanded;
    this.width = this.expanded ? DEFAULT_WIDTH : DEFAULT_MIN_WIDTH;

    if (!this.expanded) {
      this.selectExpanded = false;
    }
  }

  protected getSelectedOptionName(): string {
    const option = this.selectOptions.find(option => option.id === this.selectedOption);

    return option ? option.label : '';
  }

  protected getSelectedOptionShortName(): string {
    const option = this.selectOptions.find(option => option.id === this.selectedOption);

    return option
      ? this.getShortenedName(option.label)
      : '';
  }

  protected toggleSelect(e: Event): void {
    e.stopPropagation();
    this.selectExpanded = !this.selectExpanded;
  }

  protected closeSelect(): void {
    this.selectExpanded = false;
  }

  protected selectOption(optionId: string): void {
    if (this.selectedOption !== optionId) {
      this.optionSelected.emit(optionId);
      this.closeSelect();
    }
  }

  protected toggleProfile(): void {
    this.profileExpanded = !this.profileExpanded;
  }

  protected closeProfile(): void {
    this.profileExpanded = false;
  }

  protected closeNotifications(): void {
    this.notificationsExpanded = false;
  }

  protected getShortenedName(source: string): string {
    return source
      .split(' ')
      .slice(0, 2)
      .map(word => word[0])
      .join('');
  }

  protected openEditProfile(): void {
    this.openProfile.emit();
  }

  protected clickInvite(accept: boolean, id: string): void {
    if (accept) {
      this.acceptInvite.emit(id);
    } else {
      this.rejectInvite.emit(id);
    }
  }
}
